import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Wallet, TrendUp, Coins, Bank, FileArrowDown, ArrowsClockwise, X, Info } from "@phosphor-icons/react";

export default function Portfolio() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null); // 'withdraw' | 'reinvest'
  const [amount, setAmount] = useState("");
  const [reBrand, setReBrand] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/portfolio/me").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); api.get("/brands").then(({ data }) => { setBrands(data); setReBrand(data[0]?.slug || ""); }); }, []);

  const submitWithdraw = async () => {
    setBusy(true);
    try { await api.post("/wallet/withdraw", { amount: Number(amount) }); toast.success("Withdrawal requested — pending approval"); setModal(null); setAmount(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submitReinvest = async () => {
    setBusy(true);
    try { await api.post("/wallet/reinvest", { amount: Number(amount), brand_slug: reBrand }); toast.success("Reinvested from wallet"); setModal(null); setAmount(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <div className="px-6 py-32 text-center text-ebg-muted font-data">Loading…</div>;
  const active = data.investments.filter((i) => i.status === "active");

  return (
    <div data-testid="portfolio-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Ecosystem Partner App</p>
          <h1 className="font-display text-4xl md:text-5xl text-white">Your portfolio, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Link to="/explore" className="btn-gold px-5 py-2.5 rounded-md text-sm">Invest in another brand</Link>
      </div>

      <div className="flex items-start gap-2 mt-5 text-xs text-ebg-muted bg-gold/5 border border-gold/15 rounded-lg px-4 py-2.5">
        <Info size={15} className="text-gold flex-shrink-0 mt-0.5" /> All return figures are <b className="mx-1 text-gold">indicative, not guaranteed</b>, and pending Finance/Chairman approval.
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[
          [Coins, "Total Invested", formatINR(data.total_invested)],
          [Wallet, "Wallet Balance", formatINR(data.wallet_balance)],
          [TrendUp, "Total Earned (indicative)", formatINR(data.total_earned)],
          [Bank, "Available to Withdraw", formatINR(data.available_balance)],
        ].map(([Icon, l, v]) => (
          <div key={l} className="bg-navy-900 border border-white/10 rounded-xl p-5">
            <Icon size={22} className="text-gold" weight="light" />
            <p className="text-[0.65rem] uppercase tracking-widest text-ebg-muted mt-3">{l}</p>
            <p className="font-display text-3xl text-white mt-1">{v}</p>
          </div>
        ))}
      </div>

      {/* wallet actions */}
      <div className="flex flex-wrap gap-3 mt-6">
        <button onClick={() => setModal("withdraw")} data-testid="open-withdraw" className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-md text-sm hover:bg-gold/10 transition-colors"><Bank size={16} /> Withdraw</button>
        <button onClick={() => setModal("reinvest")} data-testid="open-reinvest" className="flex items-center gap-2 border border-white/15 text-white px-5 py-2.5 rounded-md text-sm hover:border-gold transition-colors"><ArrowsClockwise size={16} /> Reinvest / Compound</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
        {/* holdings */}
        <div className="lg:col-span-3 bg-navy-900 border border-white/10 rounded-xl p-6">
          <h2 className="font-display text-2xl text-white mb-5">Holdings</h2>
          {active.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-lg p-10 text-center">
              <p className="text-ebg-muted">No active investments yet.</p>
              <Link to="/explore" className="inline-block mt-4 btn-gold px-6 py-2.5 rounded-md text-sm">Explore Brands</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {active.map((inv) => (
                <div key={inv.id} data-testid={`holding-${inv.ref}`} className="card-franchise rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center w-11 h-11 rounded-md gold-gradient text-navy-950 font-display text-lg">{inv.brand_name?.[0]}</span>
                      <div>
                        <p className="text-white font-medium">{inv.brand_name}</p>
                        <p className="text-xs text-ebg-muted">{inv.model} · {inv.tenure}</p>
                      </div>
                    </div>
                    <a href={`${API}/investments/${inv.id}/agreement`} target="_blank" rel="noreferrer" data-testid={`agreement-${inv.ref}`} className="text-xs text-gold flex items-center gap-1 hover:underline"><FileArrowDown size={15} /> Agreement</a>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 border-t border-white/10 pt-4">
                    <div><p className="text-[0.6rem] uppercase tracking-widest text-ebg-muted">Principal</p><p className="font-data text-white text-sm mt-1">{formatINR(inv.principal)}</p></div>
                    <div><p className="text-[0.6rem] uppercase tracking-widest text-ebg-muted">Indicative ROI</p><p className="font-data text-gold text-sm mt-1">{Math.round(inv.indicative_rate*100)}% p.a.</p></div>
                    <div><p className="text-[0.6rem] uppercase tracking-widest text-ebg-muted">Next Payout</p><p className="font-data text-white text-sm mt-1">{inv.next_payout || "—"}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* transactions */}
        <div className="lg:col-span-2 bg-navy-900 border border-white/10 rounded-xl p-6">
          <h2 className="font-display text-2xl text-white mb-5">Wallet Activity</h2>
          {data.transactions.length === 0 ? (
            <p className="text-ebg-muted text-sm">No wallet activity yet. Returns appear here after the monthly payout run.</p>
          ) : (
            <div className="space-y-3">
              {data.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-white/5 pb-3">
                  <div><p className="text-sm text-white capitalize">{t.note || t.type}</p><p className="text-[0.65rem] text-ebg-muted font-data">{t.ref} · {t.created_at?.slice(0,10)}</p></div>
                  <span className={`font-data text-sm ${t.type === "payout" ? "text-emerald-300" : "text-rose-300"}`}>{t.type === "payout" ? "+" : "−"}{formatINR(t.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* modal */}
      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl text-navy-950">{modal === "withdraw" ? "Withdraw funds" : "Reinvest from wallet"}</h3>
              <button onClick={() => setModal(null)}><X size={20} className="text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Available: <b className="text-navy-950">{formatINR(data.available_balance)}</b></p>
            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Amount (₹)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="modal-amount" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-navy-950 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none" />
            {modal === "reinvest" && (
              <>
                <label className="text-xs font-medium text-gray-600 mb-1.5 mt-4 block">Into brand</label>
                <select value={reBrand} onChange={(e) => setReBrand(e.target.value)} data-testid="modal-brand" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-navy-950 focus:border-gold focus:outline-none">
                  {brands.filter((b) => b.status !== "coming_soon").map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                </select>
              </>
            )}
            <button onClick={modal === "withdraw" ? submitWithdraw : submitReinvest} disabled={busy} data-testid="modal-submit" className="w-full mt-6 btn-gold py-3 rounded-lg disabled:opacity-50">
              {busy ? "Processing…" : modal === "withdraw" ? "Request Withdrawal" : "Confirm Reinvestment"}
            </button>
            {modal === "withdraw" && <p className="text-xs text-gray-400 mt-3 text-center">Withdrawals require admin approval (maker-checker).</p>}
          </div>
        </div>
      )}
    </div>
  );
}
